import { __decorate } from "tslib";
import { CellGridSelectorComponent } from './components/cell-grid-selector/cell-grid-selector.component';
import { DialogComponent } from './components/dialog/dialog.component';
import { DynamicFormsComponent } from './components/dynamic-forms/dynamic-forms.component';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GridComponent } from './components/grid/grid.component';
import { LicensesComponent } from './components/licenses/licenses.component';
import { LoadingSpinner } from './components/loadingSpinner/loadingSpinner.component';
import { RulesComponent } from './components/rules/rules.component';
import { NgxSmartModalModule } from 'ngx-smart-modal';
import { StartCasePipe } from './pipes/startCase.pipe';
import { Injector, NgModule } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormioAppConfig, Templates, Formio } from '@formio/angular';
import { FormioGrid } from '@formio/angular/grid';
import { formioConfig } from './formio-config';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ContextMenuModule } from 'primeng/contextmenu';
import { FieldsetModule } from 'primeng/fieldset';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ListboxModule } from 'primeng/listbox';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';
import { FileUploadModule } from 'primeng/fileupload';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TreeModule } from 'primeng/tree';
import { ClipboardModule, ClipboardService } from 'ngx-clipboard';
import { GalleriaModule } from 'primeng/galleria';
import { ToastModule } from 'primeng/toast';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MessagesModule } from 'primeng/messages';
import { SailsSocketModule, SailsSocketService } from './modules/sails-socket';
import { DataUsageModule } from './modules/data-usage/data-usage.module';
import { equipmentInfoServiceProvider, dashboardInfoServiceProvider, eventInfoServiceProvider, rcuInfoServiceProvider, userInfoServiceProvider, groupServiceProvider, groupsButtonServiceProvider, organizationServiceProvider, modalServiceProvider } from './ajs-upgraded-providers';
import 'ace-builds/src-min-noconflict/ace';
import 'ace-builds/webpack-resolver';
import 'ace-builds/src-min-noconflict/ext-language_tools';
import { EquipmentGridComponent } from './components/equipment-grid/equipment-grid.component';
import { GalleriaComponent } from './components/galleria/galleria.component';
import { ManualInjector } from './manual-injector';
import { InputSwitchComponent } from './components/inputSwitch/inputSwitch.component';
import { GroupsTreeRootDirective } from './ajs-groupTreeSelector';
import { DropdownModule } from 'primeng/dropdown';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { MessagesComponent } from './components/messages/message.component';
import { MultiSelectComponent } from './components/multiSelect/multiselect.component';
import { customComponentsMapping } from './components/dynamic-forms/form-components';
import { DynamicFormatPipe } from './pipes/dynamicFormat.pipe';
import { TopNavComponent } from './components/top-nav/top-nav.component';
import { FieldSetComponent } from './components/fieldset/fieldset.component';
const defaultForm = Templates.templates.bootstrap3.panel.form;
// If there is a ts error below, it's because formiojs interface is wrong and extendTemplate does exist
// https://github.com/formio/formio.js/blob/0af704817b2d4b7931a8db3e0ea9d779b2fb9b75/types/templates.d.ts
Templates.extendTemplate('bootstrap3', {
    panel: {
        form(ctx) {
            return defaultForm(ctx).replace(/text-muted/, '');
        }
    }
});
Templates.framework = 'bootstrap3';
Templates.current.defaultIconset = 'fa'; // Font Awesome
Formio.use({
    components: customComponentsMapping,
});
let AppModule = class AppModule {
    sails;
    primengConfig;
    clipboardService;
    messageService;
    injector;
    constructor(sails, primengConfig, clipboardService, messageService, injector) {
        this.sails = sails;
        this.primengConfig = primengConfig;
        this.clipboardService = clipboardService;
        this.messageService = messageService;
        this.injector = injector;
        this.primengConfig.ripple = true;
        const { appConfig: { apiBaseUrl, pc3Env } } = window;
        const config = {
            url: apiBaseUrl,
            uri: apiBaseUrl,
            autoConnect: false,
            reconnection: true,
            transports: ['websocket'],
            useCORSRouteToGetCookie: true,
            headers: { url: window.location.hostname },
            environment: pc3Env
        };
        this.sails.connect(config.url, config).subscribe();
        ManualInjector.injector = injector;
    }
    ngDoBootstrap() {
        // Gotta implement this or suffer angry console messages
        this.handleClipboardResponse();
    }
    // Handle global copiedToClipboard response
    handleClipboardResponse() {
        this.clipboardService.copyResponse$.subscribe((res) => {
            if (res.isSuccess) {
                this.messageService.add({ severity: 'success', summary: 'Copied to clipboard' });
            }
        });
    }
    static ctorParameters = () => [
        { type: SailsSocketService },
        { type: PrimeNGConfig },
        { type: ClipboardService },
        { type: MessageService },
        { type: Injector }
    ];
};
AppModule = __decorate([
    NgModule({
        imports: [
            BrowserAnimationsModule,
            BrowserModule,
            ButtonModule,
            ContextMenuModule,
            FieldsetModule,
            FormioGrid,
            FormsModule,
            InputNumberModule,
            InputTextModule,
            ListboxModule,
            MultiSelectModule,
            NgxSmartModalModule.forRoot(),
            RippleModule,
            TableModule,
            ProgressSpinnerModule,
            TooltipModule,
            SailsSocketModule,
            DataUsageModule,
            FileUploadModule,
            HttpClientModule,
            GalleriaModule,
            ToastModule,
            TreeModule,
            ClipboardModule,
            ToastModule,
            ReactiveFormsModule,
            DropdownModule,
            InputSwitchModule,
            DynamicFormatPipe,
            GridComponent,
            DynamicFormsComponent,
            MessagesModule,
            TopNavComponent,
        ],
        declarations: [
            CellGridSelectorComponent,
            DialogComponent,
            FieldSetComponent,
            LicensesComponent,
            DialogComponent,
            StartCasePipe,
            LoadingSpinner,
            GalleriaComponent,
            EquipmentGridComponent,
            GroupsTreeRootDirective,
            RulesComponent,
            InputSwitchComponent,
            MessagesComponent,
            MultiSelectComponent,
            DropdownComponent
        ],
        entryComponents: [
            CellGridSelectorComponent,
            DialogComponent,
            FieldSetComponent,
            LicensesComponent,
            DialogComponent,
            LoadingSpinner,
            GalleriaComponent,
            EquipmentGridComponent,
            RulesComponent,
            InputSwitchComponent,
            MessagesComponent,
            MultiSelectComponent,
            DropdownComponent
        ],
        providers: [
            { provide: FormioAppConfig, useValue: formioConfig },
            equipmentInfoServiceProvider,
            dashboardInfoServiceProvider,
            eventInfoServiceProvider,
            rcuInfoServiceProvider,
            userInfoServiceProvider,
            MessageService,
            groupServiceProvider,
            groupsButtonServiceProvider,
            FormBuilder,
            organizationServiceProvider,
            modalServiceProvider,
        ]
    })
], AppModule);
export { AppModule };
